import React, { useLayoutEffect, useRef, useState } from 'react';
import { useAppSelector } from '@/redux/hooks';
import { selectOverlayLayerByIndex } from '@/redux/selectors/packagingTimelineSelectors';
import { selectPackagingIsEditingTimeline, selectPackagingTimelineScale } from '@/redux/selectors/packagingSelectors';
import { OverlayItem, OverlayLayer } from '@/redux/slices/packagingSlice.types';
import GroupMarker from '@/components/Packaging/LayeredTimeline/common/GroupMarker';

interface Props {
  layerIndex: number;
}

type Group = { left: number; right: number; item: OverlayItem }[];

const SMALL_ENOUGH_TO_BE_ADDED_TO_GROUP = 24;

const CloseOverlayGroupsMarkers: React.FC<Props> = ({ layerIndex }) => {
  const scale = useAppSelector(selectPackagingTimelineScale);
  const hideGroups = useAppSelector(selectPackagingIsEditingTimeline);
  const layer = useAppSelector((state) => selectOverlayLayerByIndex(state, layerIndex));
  const ref = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [groups, setGroups] = useState<Group[]>([]);

  useLayoutEffect(() => {
    clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      if (!ref.current?.parentNode) return;

      const elGroups: Set<HTMLElement>[] = []; // This array is needed just to make sure we don't add an element to the group twice
      const groups: Group[] = [];
      let curGroupIndex = 0;

      const overlayEls = Array.from(ref.current.parentNode.querySelectorAll<HTMLElement>('[data-overlay-id]')).sort((a, b) => {
        const aLeft = parseFloat(a.style.left);
        const bLeft = parseFloat(b.style.left);
        return aLeft > bLeft ? 1 : -1;
      });

      for (let i = 0; i < overlayEls.length; i++) {
        const cur = overlayEls[i];
        const curLeft = parseFloat(cur.style.left);
        const curWidth = parseFloat(cur.style.width);
        const next = overlayEls[i + 1];

        if (!next) continue;

        const nextLeft = parseFloat(next.style.left);
        const nextWidth = parseFloat(next.style.width);

        if (curLeft + curWidth > nextLeft) {
          if (!groups[curGroupIndex]) {
            elGroups[curGroupIndex] = new Set();
            groups[curGroupIndex] = [];
          }
          if (!elGroups[curGroupIndex].has(cur)) {
            elGroups[curGroupIndex].add(cur);
            groups[curGroupIndex].push({ left: curLeft, right: curLeft + curWidth, item: getItemByElement(cur, layer) });
          }

          if (nextWidth <= SMALL_ENOUGH_TO_BE_ADDED_TO_GROUP) {
            elGroups[curGroupIndex].add(next);
            groups[curGroupIndex].push({ left: nextLeft, right: nextLeft + nextWidth, item: getItemByElement(next, layer) });
          }
        } else {
          curGroupIndex++;
        }
      }

      setGroups(groups);
    }, 100);

    return () => {
      setGroups([]);
    };
  }, [layer, scale]);

  return (
    <div ref={ref}>
      {groups.map((group, index) => {
        const firstItem = group.at(0);
        const lastItem = group.at(-1);

        if (!firstItem || !lastItem) return;

        const start = firstItem.left;
        const end = lastItem.right;
        const overlays = group.map((g) => g.item);

        return <GroupMarker key={index} show={!hideGroups} position={start} width={end - start} items={overlays} />;
      })}
    </div>
  );
};

const getItemByElement = (overlayElement: HTMLElement, layer: OverlayLayer) => {
  const id = overlayElement.getAttribute('data-overlay-id');

  if (!id) {
    throw new Error('overlayElement should have data-overlay-id attribute with overlay id');
  }

  const overlay = layer.find((o) => o.draggableId === id);

  if (!overlay) {
    throw new Error(`Can not find overlay with "${id}" id`);
  }

  return overlay;
};

export default CloseOverlayGroupsMarkers;
